import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AdSlot } from '@/components/ad-slot';
import { Button, Modal } from '@/components/ui';
import { TelegramIcon, TelegramLoginButton } from '@/components/telegram-login';
import { t, useTranslations } from '@/shared/i18n';
import type { BotLogin } from '@/shared/use-bot-login';

const FEATURES = [
  { emoji: '🎭', key: 'anonymous' },
  { emoji: '⚡', key: 'instant' },
  { emoji: '🔒', key: 'private' },
] as const;

// Прямая ссылка на мини-приложение, а не на чат с ботом: она открывает
// приложение сразу, без промежуточного экрана с кнопкой «Запустить».
const TELEGRAM_BOT_URL =
  import.meta.env.VITE_TELEGRAM_BOT_URL ?? 'https://t.me/ghostlyimbot/chat';

/** Домен вынесен в настройки: смена адреса не требует правки кода. */
const BRAND_DOMAIN = import.meta.env.VITE_BRAND_DOMAIN ?? 'ghostly.im';
const lastDot = BRAND_DOMAIN.lastIndexOf('.');
const BRAND_NAME = lastDot > 0 ? BRAND_DOMAIN.slice(0, lastDot) : BRAND_DOMAIN;
const BRAND_ZONE = lastDot > 0 ? BRAND_DOMAIN.slice(lastDot) : '';

/**
 * Главный экран веб-версии. В отличие от мини-приложения, здесь пользователь
 * попадает «с улицы» и ничего не знает о сервисе, поэтому экран объясняет суть
 * и ведёт к единственному действию — начать чат.
 */
export function LandingScreen({
  onStart,
  botLogin,
  busy,
  error,
  heading,
}: {
  onStart: () => void;
  /** Вход через бота: своё состояние, потому что человек уходит и возвращается. */
  botLogin: BotLogin;
  busy: boolean;
  error: string | null;
  /** Заголовок из панели; пусто — остаётся домен. */
  heading?: string | null;
}): React.ReactNode {
  const dictionary = useTranslations();
  const [rulesOpen, setRulesOpen] = useState(false);

  return (
    /*
      Высота ограничена экраном, а прокрутка — внутренняя: контейнер веб-версии
      обрезает содержимое по высоте окна, и при `min-h-full` всё, что ниже
      первого экрана, оказывалось недостижимо.
    */
    <div className="h-full w-full overflow-y-auto">
      <div className="mx-auto flex min-h-full w-full max-w-3xl flex-col items-center px-6 py-16 text-center">
        <motion.div
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          className="mb-8 flex size-24 items-center justify-center rounded-3xl bg-accent/15 text-5xl sm:size-28 sm:text-6xl"
        >
          🎭
        </motion.div>

        {/*
          Заголовок — сам домен: на лендинг попадают из поиска и по ссылке,
          и адрес должен запоминаться. Зона выделена цветом, чтобы название
          читалось как знак, а не как строка адреса.
        */}
        {/*
          Домен — знак, а не заголовок. Крупная надпись остаётся на месте, но
          размечена как обычный текст: заголовком страницы должна быть строка,
          которая объясняет, что это за сервис.
        */}
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.08 }}
          className="text-[38px] font-semibold leading-none tracking-tight text-text sm:text-[56px]"
        >
          {BRAND_NAME}
          <span className="text-accent">{BRAND_ZONE}</span>
        </motion.p>

        {/*
          Заголовок страницы — видимый, а не спрятанный ради поисковых систем.
          Скрытый текст они считают приёмом накрутки, и ради серой зоны рисковать
          незачем: строка и так на экране, достаточно разметить её как заголовок.
          Уровень заголовка не влияет на размер шрифта — вид не меняется.
        */}
        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.12 }}
          className="mt-3 text-[15px] font-medium tracking-wide text-text-muted"
        >
          {heading ?? dictionary.landing.title}
        </motion.h1>

        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.16 }}
          className="mt-4 max-w-xl text-[16px] leading-relaxed text-text-muted sm:text-[18px]"
        >
          {dictionary.landing.subtitle}
        </motion.p>

        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.24 }}
          className="mt-10 flex w-full max-w-xs flex-col gap-3"
        >
          <Button disabled={busy} onClick={onStart}>
            {busy ? dictionary.common.loading : dictionary.landing.action}
          </Button>

          <TelegramLoginButton botLogin={botLogin} />

          {/*
            Третий путь и самый тихий: с телефона мини-приложение открывается
            одним нажатием, и отнимать эту дорогу у того, кто пришёл именно за
            ней, незачем. Ссылка, а не кнопка: переход во внешнее приложение
            должен работать средней кнопкой мыши и показывать адрес при
            наведении.
          */}
          <a
            href={TELEGRAM_BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[13px] text-text-muted underline-offset-2 transition-colors hover:text-text hover:underline"
          >
            {dictionary.landing.telegramOpenApp}
          </a>

          {error ? <p className="text-[13px] text-danger">{error}</p> : null}
          <p className="text-[12px] text-text-muted">{dictionary.landing.notice}</p>
        </motion.div>

        {/*
          Без анимации появления: содержимое ниже первого экрана всё равно
          открывается прокруткой, а привязывать его видимость к анимации значит
          показать пустоту, если та почему-то не отработала.
        */}
        <div className="mt-10 w-full max-w-xl rounded-2xl border border-border bg-surface px-5 py-5 text-left">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#229ED9]/15 text-[#229ED9]">
              <TelegramIcon />
            </span>
            <div>
              <p className="text-[15px] font-medium text-text">
                {dictionary.landing.telegramTitle}
              </p>
              <p className="mt-1 text-[13px] leading-snug text-text-muted">
                {dictionary.landing.telegramText}
              </p>

              {/*
                Новая возможность рассказывается здесь же, а не отдельным
                блоком: карточка и так про то, что сервис остаётся с человеком,
                — сохранённый собеседник её прямое продолжение.
              */}
              <p className="mt-3 text-[15px] font-medium text-text">
                {dictionary.landing.savedTitle}
              </p>
              <p className="mt-1 text-[13px] leading-snug text-text-muted">
                {dictionary.landing.savedText}
              </p>
            </div>
          </div>
        </div>

        <ul className="mt-8 grid w-full gap-4 sm:grid-cols-3">
          {FEATURES.map((feature) => (
            <li
              key={feature.key}
              className="rounded-2xl border border-border bg-surface px-5 py-6 text-left"
            >
              <span className="text-2xl">{feature.emoji}</span>
              <p className="mt-3 text-[15px] font-medium text-text">
                {dictionary.landing.features[feature.key].title}
              </p>
              <p className="mt-1 text-[13px] leading-snug text-text-muted">
                {dictionary.landing.features[feature.key].text}
              </p>
            </li>
          ))}
        </ul>

        {/*
          Рекламное место главной. Ниже рассказа о сервисе и выше подвала:
          кнопку входа не отодвигает, а до него доходят те, кто читает
          страницу, а не сразу нажимает.
        */}
        <AdSlot name="landing" className="mt-8 w-full" />

        {/*
          Подвал одной строкой: разнесённые по краям мелочи занимают меньше
          места, чем столбик, и не притворяются отдельным разделом.
        */}
        <div className="mt-auto flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-2
                        pt-12 text-[12px] text-text-muted sm:justify-between">
          <span>
            {t(dictionary.landing.copyright, {
              domain: BRAND_DOMAIN,
              year: new Date().getFullYear(),
            })}
          </span>

          {/* Неприметная ссылка: правила нужны редко, но найтись должны сразу. */}
          <button
            type="button"
            onClick={() => setRulesOpen(true)}
            className="underline decoration-dotted underline-offset-4 transition-colors hover:text-text"
          >
            {dictionary.landing.rulesLink}
          </button>

          {/*
            На телефоне две длинные ссылки в подвал не помещались и растягивали
            страницу вбок — она ездила влево-вправо. Там они живут внизу окна
            правил, куда человек и так заходит за тем же самым.
          */}
          <a
            href="/terms"
            className="hidden underline decoration-dotted underline-offset-4 transition-colors hover:text-text sm:inline"
          >
            {dictionary.landing.termsLink}
          </a>

          <a
            href="/privacy"
            className="hidden underline decoration-dotted underline-offset-4 transition-colors hover:text-text sm:inline"
          >
            {dictionary.landing.privacyLink}
          </a>

          <a
            href={`mailto:${dictionary.landing.contactEmail}`}
            className="underline decoration-dotted underline-offset-4 transition-colors hover:text-text"
          >
            {dictionary.landing.contactEmail}
          </a>
        </div>

        <AnimatePresence>
          {rulesOpen ? (
            <Modal open title={dictionary.rules.title} onClose={() => setRulesOpen(false)}>
              <div className="max-h-[60vh] overflow-y-auto text-left">
                <p className="mb-4 text-[13px] text-text-muted">{dictionary.rules.intro}</p>

                <ol className="flex flex-col gap-3">
                  {dictionary.rules.items.map((item, index) => (
                    <li key={item.title} className="flex gap-3">
                      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-surface-raised text-[11px] text-text-muted">
                        {index + 1}
                      </span>
                      <span>
                        <span className="block text-[14px] font-medium text-text">{item.title}</span>
                        <span className="mt-0.5 block text-[13px] leading-snug text-text-muted">
                          {item.text}
                        </span>
                      </span>
                    </li>
                  ))}
                </ol>

                <p className="mt-4 border-t border-border pt-4 text-[13px] text-text-muted">
                  {dictionary.rules.reportHint}
                </p>

                {/*
                  Полные документы — здесь же, внизу правил: на телефоне в
                  подвале для них нет места, а человек, открывший правила, за
                  тем и пришёл.
                */}
                <div className="mt-4 flex flex-col gap-2 border-t border-border pt-4">
                  <a
                    href="/terms"
                    className="text-[13px] text-accent underline decoration-dotted underline-offset-4"
                  >
                    {dictionary.landing.termsLink}
                  </a>
                  <a
                    href="/privacy"
                    className="text-[13px] text-accent underline decoration-dotted underline-offset-4"
                  >
                    {dictionary.landing.privacyLink}
                  </a>
                </div>
              </div>

              <div className="mt-5">
                <Button onClick={() => setRulesOpen(false)}>{dictionary.rules.close}</Button>
              </div>
            </Modal>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
}
